/**
 * Общий редактор для CashReceipt (ПКО) и CashPayment (РКО).
 * Структура зеркальная BankDocEditor, но с полями кассы:
 * cash_register_id, received_from/issued_to, basis, employee_id.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cashReceipts, cashPayments,
  type CashReceiptCreate, type CashPaymentCreate,
  type ReceiptOperationType, type PaymentOperationType,
} from '@/api/cashDocs';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { employees as employeesApi } from '@/api/hr';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import ProjectSelect from '@/components/ProjectSelect';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface Props {
  kind: 'receipt' | 'payment';
}

const RECEIPT_OPS: ReceiptOperationType[] = ['FROM_BUYER', 'FROM_BANK', 'FROM_PODOTCHET', 'LOAN', 'OTHER'];
const PAYMENT_OPS: PaymentOperationType[] = [
  'TO_SUPPLIER', 'TO_BANK', 'TO_PODOTCHET', 'SALARY', 'LOAN_RETURN', 'OTHER',
];

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  cash_register_id: string;
  operation_type: string;
  contractor_id: string;
  contract_id: string;
  employee_id: string;  // для SALARY / підзвіт (резолв індивід. рах. розрахунків)
  amount: string;
  party_text: string;   // received_from или issued_to
  basis: string;
  comment: string;
  project_id: string | null;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  cash_register_id: '',
  operation_type: '',
  contractor_id: '',
  contract_id: '',
  employee_id: '',
  amount: '0',
  party_text: '',
  basis: '',
  comment: '',
  project_id: null,
};

export default function CashDocEditor({ kind }: Props) {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  // обе API одинаковой формы; даём типу свободу пройти через мутации
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const api: any = kind === 'receipt' ? cashReceipts : cashPayments;
  const listKey = kind === 'receipt' ? 'cash-receipts' : 'cash-payments';
  const itemKey = kind === 'receipt' ? 'cash-receipt' : 'cash-payment';
  const baseRoute = kind === 'receipt' ? '/cash-receipts' : '/cash-payments';
  const opOptions = kind === 'receipt' ? RECEIPT_OPS : PAYMENT_OPS;
  const defaultOp = kind === 'receipt' ? 'FROM_BUYER' : 'TO_SUPPLIER';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX permissions-by-org α: фільтр org dropdown по per-org permission.
  // Без цього user бачив усі orgs accessible через user_access, але save
  // міг впасти на 403 (per-org permission відсутній). Тепер dropdown
  // показує лише ті org, де реально можна створити ПКО/РКО.
  const requiredPerm = kind === 'receipt' ? 'cash.pko.manage' : 'cash.rko.manage';
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg(requiredPerm);
  const cList = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const docQuery = useQuery({
    queryKey: [itemKey, id],
    queryFn: () => api.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const crQ = useQuery({
    queryKey: ['cash-registers', form.organization_id],
    queryFn: () => refs.cashRegisters(form.organization_id),
    enabled: !!form.organization_id,
  });
  // Операції з працівником — для них показуємо вибір співробітника
  // (резолв індивід. рах. розрахунків при виплаті ЗП / підзвіт).
  const EMP_OPS = ['SALARY', 'TO_PODOTCHET', 'FROM_PODOTCHET'];
  const showEmployee = EMP_OPS.includes(form.operation_type);
  const empQ = useQuery({
    queryKey: ['cash-employees', form.organization_id],
    queryFn: () => employeesApi.list({
      organization_id: form.organization_id, active_only: true, limit: 500,
    }),
    enabled: !!form.organization_id && showEmployee,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  useEffect(() => {
    if (isNew) {
      // UX α: defaultну org беремо з availableOrgs (там де є permission),
      // НЕ з orgList. Інакше форма стартує з org де save заборонено.
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        operation_type: f.operation_type || defaultOp,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        cash_register_id: d.cash_register_id,
        operation_type: d.operation_type,
        contractor_id: d.contractor_id ?? '',
        contract_id: d.contract_id ?? '',
        employee_id: d.employee_id ?? '',
        amount: String(d.amount),
        party_text: (kind === 'receipt' ? d.received_from : d.issued_to) ?? '',
        basis: d.basis ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
      });
    }
  }, [isNew, docQuery.data, availableOrgs, defaultOp, kind]);

  // подставить первую кассу
  useEffect(() => {
    if (isNew && !form.cash_register_id && crQ.data && crQ.data.length > 0) {
      const first = crQ.data[0];
      if (first) setForm((f) => ({ ...f, cash_register_id: first.id }));
    }
  }, [isNew, crQ.data, form.cash_register_id]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const buildPayload = () => {
    const partyKey = kind === 'receipt' ? 'received_from' : 'issued_to';
    return {
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      cash_register_id: form.cash_register_id,
      operation_type: form.operation_type as ReceiptOperationType & PaymentOperationType,
      contractor_id: form.contractor_id || null,
      contract_id: form.contract_id || null,
      employee_id: form.employee_id || null,
      amount: form.amount,
      [partyKey]: form.party_text || null,
      basis: form.basis || null,
      comment: form.comment || null,
      project_id: form.project_id,
    };
  };

  const create = useMutation({
    mutationFn: () => api.create(buildPayload() as CashReceiptCreate & CashPaymentCreate),
    onSuccess: (doc: { id: string }) => {
      qc.invalidateQueries({ queryKey: [listKey] });
      navigate(`${baseRoute}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => api.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: [itemKey, id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: () => api.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [itemKey, id] });
      qc.invalidateQueries({ queryKey: [listKey] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => api.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [itemKey, id] });
      qc.invalidateQueries({ queryKey: [listKey] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [listKey] });
      navigate(baseRoute);
    },
  });

  const titleNew = kind === 'receipt' ? t('cr.new') : t('cp.new');
  const titleList = kind === 'receipt' ? t('cr.title') : t('cp.title');
  const partyLabel = kind === 'receipt' ? t('cr.received_from') : t('cp.issued_to');

  // UX α: warning якщо новий документ а user не має permission у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{titleNew}</h2>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати {kind === 'receipt' ? 'ПКО' : 'РКО'}{' '}
            у жодній з ваших організацій ({totalAccessibleOrgs}).
            Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate(baseRoute)}>{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? titleNew : `${titleList}: ${form.document_number}`}
          {isPosted && <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>✓ {t('mo.posted_yes')}</span>}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate(baseRoute)}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => post.mutate()}
          postPending={post.isPending}
          postDisabled={!(Number(form.amount) > 0)}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          deleteTitle="Помилкове введення — приховати з типового списку"
          onPrint={() => navigate(`${baseRoute}/${id}/print`)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('cd.organization')}</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value, cash_register_id: '' })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>{t('cd.number')}</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>{t('cd.date')}</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('cd.cash_register')}</label>
          <select value={form.cash_register_id}
                  onChange={(e) => setForm({ ...form, cash_register_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {crQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('cd.operation_type')}</label>
          <select value={form.operation_type}
                  onChange={(e) => setForm({ ...form, operation_type: e.target.value })}
                  disabled={isPosted}>
            {opOptions.map((op) => (
              <option key={op} value={op}>{t(`cd.ops.${op}`)}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('cd.amount')}</label>
          <input type="number" step="0.01" value={form.amount}
                 onChange={(e) => setForm({ ...form, amount: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('cd.contractor')}</label>
          <select value={form.contractor_id}
                  onChange={(e) => setForm({ ...form, contractor_id: e.target.value, contract_id: '' })}
                  disabled={isPosted}>
            <option value="">—</option>
            {cList.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('cd.contract')}</label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">—</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        {showEmployee && (
          <div className="field">
            <label>Працівник</label>
            <select value={form.employee_id}
                    onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                    disabled={isPosted}
                    title="Для виплати ЗП — індивідуальний рахунок розрахунків працівника підставиться у проводку (правило/картка/661).">
              <option value="">—</option>
              {empQ.data?.items.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name ?? e.code ?? e.id}{e.code && e.full_name ? ` (${e.code})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="field">
        <label>{partyLabel}</label>
        <input value={form.party_text}
               onChange={(e) => setForm({ ...form, party_text: e.target.value })}
               disabled={isPosted}
               placeholder={t('cd.party_placeholder')} />
      </div>

      <div className="field">
        <label>{t('cd.basis')}</label>
        <input value={form.basis}
               onChange={(e) => setForm({ ...form, basis: e.target.value })}
               disabled={isPosted} />
      </div>

      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
